import { useContext, useEffect, useState } from "react";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import GlobalContext from "src/context/global-context";
import { IKwickpayTransaction, IMTSTable } from "src/lib/constants/table-type";
import { IMTS, PaginationInput } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import client from "../../api";
import { useMutation } from "react-query";
import { capitalizeFirstLetter, getErrorMessage, logger } from "src/lib/utils";
import { ColumnsType } from "antd/es/table";
import { Table } from "antd";
import { ECDH } from "crypto";

const MTSListPage = ({
  searchText,
  onSelectedInvoiceData,
  onSelectedUser,
  searchField,
  startDate,
  endDate,
  isBuisiness,
  timezone,
}: {
  searchText: string;
  onSelectedInvoiceData: any;
  onSelectedUser: any;
  searchField: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isBuisiness?: boolean;
  timezone: string;
}) => {
  const navigate = useNavigate();

  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<IMTS[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const [total, setTotal] = useState(0);

  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.transactions.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg
          );
        }
        setIsAlertOpened(true);
      },
    });

  const receiptBtn = (record: any) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    logger.log("RECORD", record);
    setDownloadingId(record.id);
    downloadReceipt(
      {
        type: "mts_transaction",
        id: record.id,
        timezone: timezone,
      },
      {
        onSettled: () => setDownloadingId(null),
      }
    );
  };

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText]);

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const { mutate: getList, isLoading } = useMutation(
    isBuisiness ? client.mts.list : client.mts.list,
    {
      onSuccess: (data) => {
        setTotal(data.total);
        setDataSource(
          data.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            request_id: item.request_id,
            channel: item.channel,
            created_at: item.created_at,
            updated_at: item.updated_at,
            sender_full_name: `${item.sender_first_name} ${item.sender_last_name}`,
            sender_dob: item.sender_dob,
            sender_personal_number: item.sender_personal_number,
            sender_currency: item.sender_currency,
            sender_amount: item.sender_amount,
            sender_bank_code: item.sender_bank_code,
            sender_bank_name: item.sender_bank_name,
            receiver_full_name: `${item.receiver_first_name} ${item.receiver_last_name}`,
            receiver_dob: item.receiver_dob,
            receiver_personal_number: item.receiver_personal_number,
            receiver_currency: item.receiver_currency,
            receiver_iban: item.receiver_iban,
            receiver_amount: item.receiver_amount,
            commission_amount: item.commissionAmount,
            commission_currency: item.commissionCurrency,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      created_start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      created_end_date: endDate
        ? moment(endDate).format("yyyy-MM-DD")
        : undefined,
      search_value: searchText.length > 0 ? searchText : undefined,
      timezone: timezone,
    });
  }, [isMounted, canViewTransactions, startDate, endDate, searchText]);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText]);

  const columns: ColumnsType<IMTSTable> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Request ID
        </p>
      ),
      dataIndex: "request_id",
      className: "w-auto",
      key: "request_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Channel
        </p>
      ),
      dataIndex: "channel",
      className: "w-auto",
      key: "channel",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Commission
        </p>
      ),
      dataIndex: "commission_amount",
      className: "w-auto",
      key: "commission_amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Commission Currency
        </p>
      ),
      dataIndex: "commission_currency",
      className: "w-auto",
      key: "commission_currency",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value && capitalizeFirstLetter(value?.replaceAll("_", " "))}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Name
        </p>
      ),
      dataIndex: "sender_full_name",
      className: "w-auto",
      key: "sender_full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender DOB
        </p>
      ),
      dataIndex: "sender_dob",
      className: "w-auto",
      key: "sender_dob",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Personal Number
        </p>
      ),
      dataIndex: "sender_personal_number",
      className: "w-auto",
      key: "sender_personal_number",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Currency
        </p>
      ),
      dataIndex: "sender_currency",
      className: "w-auto",
      key: "sender_currency",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value && capitalizeFirstLetter(value.replaceAll("_", " "))}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "sender_amount",
      className: "w-auto",
      key: "sender_amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Bank Code
        </p>
      ),
      dataIndex: "sender_bank_code",
      className: "w-auto",
      key: "sender_bank_code",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Bank Name
        </p>
      ),
      dataIndex: "sender_bank_name",
      className: "w-auto",
      key: "sender_bank_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Name
        </p>
      ),
      dataIndex: "receiver_full_name",
      className: "w-auto",
      key: "receiver_full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver DOB
        </p>
      ),
      dataIndex: "receiver_dob",
      className: "w-auto",
      key: "receiver_dob",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Personal Number
        </p>
      ),
      dataIndex: "receiver_personal_number",
      className: "w-auto",
      key: "receiver_personal_number",
      render: (value) => (
        <div
          className={`bg-[${
            value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
          }] inline-block py-[4px] px-[12px] rounded-[10px]`}
        >
          <p
            className={`font-manrope ${
              value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
            } text-[12px] font-medium leading-none`}
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Amount
        </p>
      ),
      dataIndex: "receiver_amount",
      className: "w-auto",
      key: "receiver_amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Currency
        </p>
      ),
      dataIndex: "receiver_currency",
      className: "w-auto",
      key: "receiver_currency",
      render: (value) => {
        return (
          <div
            className={`bg-[${
              value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
            }] inline-block py-[4px] px-[12px] rounded-[10px]`}
          >
            <p
              className={`font-manrope ${
                value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
              } text-[12px] font-medium leading-none`}
            >
              {value && capitalizeFirstLetter(value.replaceAll("_", " "))}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Iban
        </p>
      ),
      dataIndex: "receiver_iban",
      className: "w-auto",
      key: "receiver_iban",
      render: (value) => {
        return (
          <div
            className={`bg-[${
              value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
            }] inline-block py-[4px] px-[12px] rounded-[10px]`}
          >
            <p
              className={`font-manrope ${
                value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
              } text-[12px] font-medium leading-none`}
            >
              {value && capitalizeFirstLetter(value.replaceAll("_", " "))}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {moment(value).local().format("DD/MM/YYYY H:mm")}
            {/* {moment(value).utcOffset(240).format("DD/MM/YYYY H:mm")} */}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated
        </p>
      ),
      dataIndex: "updated_at",
      className: "w-auto",
      key: "updated_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {moment(value).local().format("DD/MM/YYYY H:mm")}
            {/* {moment(value).utcOffset(240).format("DD/MM/YYYY H:mm")} */}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receipt
        </p>
      ),
      dataIndex: "receipt",
      className: "w-auto",
      key: "receipt",
      render: (_, record) =>
        !canDownload ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">---</p>
        ) : downloadingId === Number(record.id) ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">
            Downloading...
          </p>
        ) : (
          <a
            onClick={() => receiptBtn(record)}
            className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
          >
            Receipt
          </a>
        ),
    },
  ];

  const mobileColumns: ColumnsType<IMTSTable> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          MTS
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-2 justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  ID
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.id}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Request ID
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.request_id || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Channel
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.channel || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_full_name || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData &&
                    capitalizeFirstLetter(
                      selData.sender_currency.replaceAll("_", " ")
                    )}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender DOB
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_dob}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Personal Number
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_personal_number}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_currency}
                </p>
              </div>
            </div>

            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Amount
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_amount || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Bank Code
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_bank_code || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Sender Bank Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.sender_bank_name || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_full_name || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver DOB
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_dob || "---"}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Personal Number
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_personal_number || "---"}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_currency &&
                    capitalizeFirstLetter(
                      selData.receiver_currency.replaceAll("_", " ")
                    )}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receiver Iban
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.receiver_iban &&
                    capitalizeFirstLetter(
                      selData.receiver_iban.replaceAll("_", " ")
                    )}
                </p>
              </div>

              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Created
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {moment(selData.created_at).local().format("DD/MM/YYYY H:mm")}
                  {/* {moment
                    .utc(selData.created_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")} */}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Updated
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {moment(selData.created_at).local().format("DD/MM/YYYY H:mm")}
                  {/* {moment
                    .utc(selData.updated_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")} */}
                </p>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  if (!canViewTransactions) {
    return (
      <div className="custom-scroll bg-white rounded-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view transactions.
      </div>
    );
  }

  return (
    <div className="custom-scroll bg-white rounded-[12px] w-full">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? mobileColumns : columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default MTSListPage;
